import { API_ROUTES } from "@/config/apiRoutes";
import { AUTH_STORAGE_KEYS } from "@/Utils/Auth/authConstants";

export interface AuthOrganisation {
  created_at?: string;
  name: string;
  org_id: number;
  owner_id?: number;
  role?: "owner" | "admin" | "member";
  slug: string;
  updated_at?: string;
}

export interface AuthUser {
  email: string;
  full_name: string | null;
  organisation: AuthOrganisation | null;
  user_id: number;
}

const USER_STORAGE_KEY = AUTH_STORAGE_KEYS.USER;

/**
 * Reads the persisted (non-sensitive) user for optimistic hydration on reload.
 * The auth tokens themselves live in httpOnly cookies, never in JS-readable
 * storage. Returns null when nothing valid is stored.
 */
export function readStoredUser(): AuthUser | null {
  if (typeof window === "undefined") {
    return null;
  }

  const raw = window.localStorage.getItem(USER_STORAGE_KEY);

  if (!raw) {
    return null;
  }

  try {
    const user = JSON.parse(raw) as AuthUser;

    if (typeof user.user_id !== "number" || typeof user.email !== "string") {
      return null;
    }

    return user;
  } catch {
    clearStoredUser();
    return null;
  }
}

/** Persists the non-sensitive user object (never tokens). */
export function writeStoredUser(user: AuthUser): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

/** Removes the persisted user. */
export function clearStoredUser(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(USER_STORAGE_KEY);
}

/**
 * Asks the backend to rotate the token pair using the httpOnly refresh cookie.
 * The server re-sets the cookies; nothing is read or stored in JS. Returns
 * whether the refresh succeeded.
 */
export async function refreshSession(): Promise<boolean> {
  try {
    const response = await fetch(API_ROUTES.auth.refresh, {
      method: "POST",
      credentials: "include",
    });

    return response.ok;
  } catch {
    return false;
  }
}

/** Resolves the current user from the access-token cookie via /users/me. */
async function requestCurrentUser(): Promise<AuthUser | null> {
  const response = await fetch(API_ROUTES.users.me, { credentials: "include" });

  if (!response.ok) {
    return null;
  }

  const data = (await response.json()) as { user?: AuthUser };

  if (!data.user || typeof data.user.user_id !== "number") {
    return null;
  }

  return data.user;
}

/**
 * Resolves the current user from the httpOnly session cookie. If the access
 * token has expired, transparently refreshes once and retries. Returns null
 * when no valid session exists. Used to hydrate the store on boot.
 */
export async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    const user = await requestCurrentUser();

    if (user) {
      return user;
    }

    const refreshed = await refreshSession();

    return refreshed ? await requestCurrentUser() : null;
  } catch {
    return null;
  }
}
